import type { Surface, ZoneConfig, ZoneId, ZoneLeg } from "../config/types";
import { surfaceAt, type SurfaceRegion, type TrailSlice } from "./surface";
import { trailCurve } from "./trail";
import { zoneAt, type ZoneInfo } from "./zones";

export interface Mountain {
    seed: number;
    zoneAt(yM: number): ZoneInfo;
    trailAt(yM: number): TrailSlice | null;
    baseSurfaceAt(yM: number): Surface;
    surfaceAt(xM: number, yM: number, regions?: SurfaceRegion[]): Surface;
}

const lerp = (a: number, b: number, t: number) => a + (b - a) * t;

export function createMountain(
    seed: number,
    legs: ZoneLeg[],
    endless: boolean,
    zones: Record<ZoneId, ZoneConfig>,
): Mountain {
    const infoAt = (yM: number) => zoneAt(yM, legs, endless);
    const zonePair = (yM: number) => {
        const info = infoAt(yM);
        return { info, current: zones[info.zone], next: info.next ? zones[info.next] : null };
    };

    const wiggleAt = (yM: number) => {
        const { info, current, next } = zonePair(yM);
        return lerp(current.trail?.wiggle ?? 0, next?.trail?.wiggle ?? 0, info.blend);
    };

    const trailAt = (yM: number): TrailSlice | null => {
        const { info, current, next } = zonePair(yM);
        const width = lerp(current.trail?.width ?? 0, next?.trail?.width ?? 0, info.blend);
        const trail = info.blend > 0.5 ? (next?.trail ?? current.trail) : (current.trail ?? next?.trail);
        if (!trail || width <= 0) return null;
        return { x: trailCurve(seed, yM, wiggleAt).x, halfWidth: width / 2, surface: trail.surface };
    };

    const baseSurfaceAt = (yM: number): Surface => {
        const { info, current, next } = zonePair(yM);
        return info.blend > 0.5 && next ? next.baseSurface : current.baseSurface;
    };

    return {
        seed,
        zoneAt: infoAt,
        trailAt,
        baseSurfaceAt,
        surfaceAt: (xM, yM, regions = []) => surfaceAt(xM, yM, regions, trailAt(yM), baseSurfaceAt(yM)),
    };
}
